import React from 'react';
import {
  Timeline,
  Text,
  List,
  Button,
  ThemeIcon,
  Badge,
  Flex,
} from '@mantine/core';
import { IconRun, IconCheck, IconPlayerPlay, IconArrowRight, IconClock } from '@tabler/icons-react';
import colors from '../utils/colors';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ctaPulse, fadeInFromTop, heartbeat, heartbeatSmall } from '../utils/animations';

interface Props {
  sessions: Array<{
    id: string;
    title: string;
    description?: string;
    durationMinutes?: number;
    isCompleted?: boolean;
    exercises?: Array<{
      name: string;
      done?: boolean;
      skipped?: boolean;
    }>;
    finishedAt?: string | null;
  }>;
  activeIndex: number | null;
  completionDates: Record<string, Date>;
  isReadOnly?: boolean;
  onSessionClick?: (sessionId: string, idx?: number) => void;
  showBigPlay?: boolean;
  isFinishedPlan?: boolean;
}

const TrainingTimelineView: React.FC<Props> = ({
  sessions,
  activeIndex,
  completionDates,
  isReadOnly = false,
  onSessionClick,
  showBigPlay = false,
  isFinishedPlan = false
}) => {
  const { translate } = useLanguage();
  const isCompletedLocal = (s: Props['sessions'][number]) =>
    Array.isArray(s.exercises) && s.exercises.length > 0
      ? s.exercises.every((e) => e.done || e.skipped)
      : !!s.isCompleted;

  return (
    <Timeline bulletSize={24} lineWidth={3} active={activeIndex ?? -1} color={colors.green}>
      {sessions.map((s, i) => {
        const done = !!completionDates[s.id] || isCompletedLocal(s);
        const active = !done && !isReadOnly && activeIndex === i;
        const future = !done && !active;

        const bulletColor = done
          ? colors.green
          : active
          ? colors.custom36p
          : colors.dark;
        const bulletIcon = done ? <IconCheck size={14}/> : active ? <IconPlayerPlay size={14}/> : <IconRun size={14}/>;
        const lineVar: 'solid' | 'dashed' = done ? 'solid' : 'dashed';

        let badge: React.ReactNode = null;
        if (done && (completionDates[s.id] || s.finishedAt))
          badge = (
            <Badge color={colors.green} variant="light" size="md" mt={2}>
              {translate('completionDate', { date: completionDates[s.id].toLocaleDateString('pl-PL', { day: '2-digit', month:'short', year:'numeric' }) })}
            </Badge>
          );
        else if (active)
          badge = (
            <motion.div>
              <Badge color={bulletColor} variant="light" size="md" mt={2}>
                {translate('activeBadge')}
              </Badge>
            </motion.div>
          );

        return (
          <Timeline.Item
            key={s.id}
            lineVariant={lineVar}
            bullet={
              <ThemeIcon size="sm" radius="xl" color={bulletColor} variant="filled">
                {bulletIcon}
              </ThemeIcon>
            }
            pb="md"
            ml={'md'}
          >
            <div className="relative flex items-start">
              <div className="flex-1 pr-3">
                <Flex justify="space-between" align="center">
                  <Text 
                    fw={600} 
                    c={done || active ? bulletColor : '#333333'} 
                  >
                    {s.title}
                  </Text>
                  {badge}
                </Flex>
                
                <Flex align="center" gap={4} mt={4} mb={2}>
                  <IconClock size={14} color={colors.gray} />
                  <Text size="xs" c="dimmed">
                    {translate('sessionDuration', { duration: s.durationMinutes ?? 0 })}
                  </Text>
                </Flex>
                
                <List 
                  size="xs" 
                  spacing={1} 
                  mt={5}
                  ml={20}
                  className="pl-4"
                  styles={{
                    itemWrapper: { marginBottom: '2px' }
                  }}
                  icon={
                    <div className="w-2 h-2 rounded-full border border-gray-400"></div>
                  }
                >
                  {(s.exercises ?? []).map((ex, idx) => (
                    <List.Item 
                      key={`${s.id}-${idx}`} 
                      className="text-gray-600 font-medium"
                      icon={
                        ex.done ? (
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        ) : ex.skipped ? (
                          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                        ) : (
                          <div className="w-2 h-2 rounded-full border border-gray-400"></div>
                        )
                      }
                    >
                      {ex.name}
                    </List.Item>
                  ))}
                </List>

                {onSessionClick && (
                  <motion.div animate="animate" variants={active ? heartbeat : heartbeatSmall} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="light"
                      color={bulletColor}
                      size="xs"
                      leftSection={<IconArrowRight size={14} />}
                      onClick={() => onSessionClick(s.id, i)}
                      mt="xs"
                    >
                      {translate('enter')}
                    </Button>
                  </motion.div>
                )}
              </div>
            </div>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
};

export default TrainingTimelineView;
